
import { ChangeEvent, useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { downloadBase64File } from "../../../../common/utils/download_utils";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useNovosNegociosCadastroPagination } from "../hooks/novos_negocios_cadastro/pagination";
import { defaultNovosNegociosForm, NovosNegociosForm, NovosNegociosSchema } from "../schemas/Novos_negocios_cadastro/novosNegociosCadastro";
import { NovosNegociosArquivoLista } from "../../domain/models/novos_negocios_arquivo_lista";
import { NovosNegocios } from "../../domain/models/novos_negocios";
import { NovosNegociosArquivo } from "../../domain/models/novos_negocios_arquivo";
import { NovosNegociosArquivoForm } from "../schemas/Novos_negocios_cadastro/novosArquivoCadastro";

export const useNovosNegociosCadastroViewModel = () => {
    const {
        commons: {
            getCentroCustoDropDownUseCase,
            getClientesDropDownUseCase,
            getFuncionariosDropDownUseCase,
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,

        },
        comercial: {
            getNovosNegociosArquivoByIdUseCase,
            createNovosNegociosArquivoUseCase,
            updateNovosNegociosArquivoUseCase,
            deleteNovosNegociosArquivoUseCase,
            getNovosNegociosByIdUseCase,
            createNovosNegociosUseCase,
            updateNovosNegociosUseCase,
            deleteNovosNegociosUseCase,
            getAllNovosNegociosArquivoUseCase,
            downloadNovosNegociosArquivoUseCase,
        },
    } = useDependencyInjection();

    const location = useLocation();
    const navigate = useNavigate();

    const menuRef = useRef<HTMLButtonElement>(null);
    const pagination = useNovosNegociosCadastroPagination();

    const [openMenu, setOpenMenu] = useState(false);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const { pushErrorNotification, pushInfoNotification, setCustomLoading } = usePageLoader();

    const [novosNegociosSelectedId, setNovosNegociosSelectedId] = useState<number | undefined>();
    const [novosNegociosArquivoSelectedIdDelete, setNovosNegociosArquivoSelectedIdDelete] = useState<number | undefined>();


    const [novosNegocios, setNovosNegocios] = useState<NovosNegocios | undefined>();
    const [novosNegociosArquivo, setNovosNegociosArquivo] = useState<NovosNegociosArquivo | undefined>();

    const [novosNegociosArquivoLista, setNovosNegociosArquivoLista] = useState<NovosNegociosArquivoLista[]>([]);

    // MODAIS
    const [openDeleteNovosNegociosModal, setOpenDeleteNovosNegociosModal] = useState<boolean>(false);
    const [openDeleteNovosNegociosArquivoModal, setOpenDeleteNovosNegociosArquivoModal] = useState<boolean>(false);

    const [openDeleteArquivoModal, setOpenDeleteArquivoModal] = useState<boolean>(false);
    const [openNovosNegociosArquivoModal, setNovosNegociosArquivoModal] = useState<boolean>(false);

    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlCliente, setDdlCliente] = useState<DropDown[]>();
    const [ddlFuncionario, setDdlFuncionario] = useState<DropDown[]>();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification("Erro", [e]),
    });

    const hookFormNovosNegocios = useForm<NovosNegociosForm>({
        reValidateMode: "onSubmit",
        mode: "onSubmit",
        defaultValues: defaultNovosNegociosForm,
        resolver: zodResolver(NovosNegociosSchema),
    });

    const [situacaoProposta] = hookFormNovosNegocios.watch(['situacaoProposta']);


    //#region CARREGAMENTO 

    const loadNovosNegocios = useCallback(async (idNovosNegocios: number) => {
        try {
            const cliente = await getNovosNegociosByIdUseCase.execute(idNovosNegocios);
            for (const [key, value] of Object.entries(cliente)) {
                hookFormNovosNegocios.setValue(key as any, value ?? null);
            }

            setNovosNegocios(cliente);

        } catch {
            pushErrorNotification("Erro", "Falha ao buscar registro da novos negócios!");
        }
    }, [hookFormNovosNegocios,
        getNovosNegociosByIdUseCase,
        pushErrorNotification]);


    const loadNovosNegociosArquivo = useCallback(async (idNovosNegociosArquivo: number) => {
        try {
            const itemParaEditar = novosNegociosArquivoLista.find(x => x.idNovosNegociosArquivo === idNovosNegociosArquivo);
            if (itemParaEditar) setNovosNegociosArquivo(itemParaEditar);
            // const novosNegociosArquivo = await getNovosNegociosArquivoByIdUseCase.execute(idNovosNegociosArquivo);
            // setNovosNegociosArquivo(novosNegociosArquivo);
        } catch {
            pushErrorNotification("Erro", "Falha ao buscar registro de arquivo!");
        }
    }, [novosNegociosArquivoLista, pushErrorNotification]);


    const loadAllNovosNegociosArquivo = useCallback(async (idNovosNegocios: number, currentPage?: number, rowsPerPage?: number) => {
        try {
            await getAllNovosNegociosArquivoUseCase.execute(idNovosNegocios, currentPage, rowsPerPage);
            setNovosNegociosArquivoLista(getAllNovosNegociosArquivoUseCase.items);
        } catch {
            pushErrorNotification("Erro", "Falha ao buscar registro de arquivos do novos negócios!");
        }
    }, [
        getAllNovosNegociosArquivoUseCase,
        pushErrorNotification
    ]);

    const loadCentroCustoDropDown = useCallback(async () => {
        try {
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar centro de custo!"));
        }
    }, [getCentroCustoDropDownUseCase, pushErrorNotification]);

    const loadClientesDropDown = useCallback(async () => {
        try {
            const result = await getClientesDropDownUseCase.getClientesDropDown();
            setDdlCliente(result);
        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar clientes !"));
        }
    }, [getClientesDropDownUseCase, pushErrorNotification]);

    const loadFuncionarioDropDown = useCallback(async () => {
        try {
            const result = await getFuncionariosDropDownUseCase.getFuncionariosDropDown();
            setDdlFuncionario(result.map(f => ({
                id: f.idFuncionario,
                title: f.nome
            })));

        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar funcionários!"));
        }
    }, [getFuncionariosDropDownUseCase, pushErrorNotification]);


    //#endregion




    //#region  PAGE START
    usePageStartBootstrap(async () => {
        try {
            await loadCentroCustoDropDown();
            await loadClientesDropDown();
            await loadFuncionarioDropDown();

            const idNovosNegocios = novosNegociosSelectedId ?? (location.state?.idNovosNegocios as number);

            if (idNovosNegocios) {
                setNovosNegociosSelectedId(idNovosNegocios);
                await loadNovosNegocios(idNovosNegocios);
                await loadAllNovosNegociosArquivo(idNovosNegocios);
            } else {
                hookFormNovosNegocios.reset();
                setNovosNegocios(undefined);
                setNovosNegociosSelectedId(undefined);
            }
        } catch (e: unknown) {
            console.log(e);
        }
    });


    //#endregion





    //#region CABEÇALHO

    const handlePageBack = useCallback(() => {
        navigate('/NovosNegocios_lista')
    }, [navigate]);

    const handleReload = useCallback(async () => {
        if (novosNegociosSelectedId) {
            await loadNovosNegocios(novosNegociosSelectedId);
            await loadAllNovosNegociosArquivo(novosNegociosSelectedId);
        }
    }, [
        novosNegociosSelectedId,
        loadNovosNegocios,
        loadAllNovosNegociosArquivo
    ]);


    const handleNewNovosNegocios = useCallback(() => {
        hookFormNovosNegocios.reset();
        setNovosNegocios(undefined);
        setNovosNegociosSelectedId(undefined);
        setNovosNegociosArquivoLista([]);

    }, [hookFormNovosNegocios]);


    const handleCloseMenu = useCallback((event: any) => {
        if (
            menuRef.current &&
            menuRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        setOpenMenu(false);
    }, []);

    const handleToggleMenu = useCallback(() => {
        setOpenMenu((prev) => !prev);
    }, []);

    const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpenMenu(false);
        } else if (event.key === 'Escape') {
            setOpenMenu(false);
        }
    }, []);

    //#endregion




    //#region TABELAS ARQUIVOS 

    const handleNovosNegociosArquivoPageChange = useCallback((newPage: number) => {
        loadAllNovosNegociosArquivo(novosNegociosSelectedId!, newPage + 1, pagination.novosNegociosArquivos.rowsPerPage);
    }, [
        loadAllNovosNegociosArquivo,
        novosNegociosSelectedId,
        pagination.novosNegociosArquivos.rowsPerPage
    ]);


    const handleNovosNegociosArquivoRowsPerPageChange = useCallback((newRowsPerPage: number) => {
        loadAllNovosNegociosArquivo(novosNegociosSelectedId!, pagination.novosNegociosArquivos.page, newRowsPerPage);
    }, [
        loadAllNovosNegociosArquivo,
        novosNegociosSelectedId,
        pagination.novosNegociosArquivos.page
    ]);


    //#endregion




    //#region SALVAR/UPDATE

    const handleSalvarNovosNegocios = useCallback(async (form: NovosNegociosForm) => {
        try {

            setSaving(true);
            setLoading(true);
            if (novosNegociosSelectedId) {
                await updateNovosNegociosUseCase.execute({
                    idNovosNegocios: novosNegociosSelectedId,
                    nrNegocio: form.nrNegocio,
                    titulo: form.titulo ?? undefined,
                    idCliente: form.idCliente ?? undefined,
                    idCentroCusto: form.idCentroCusto ?? undefined,
                    nProposta: form.nProposta ?? undefined,
                    nEdital: form.nEdital ?? undefined,
                    possivelEscopo: form.possivelEscopo ?? undefined,
                    viavelFinanc: form.viavelFinanc ?? undefined,
                    possivelPrazos: form.possivelPrazos ?? undefined,
                    possivelReqLegais: form.possivelReqLegais ?? undefined,
                    comentarios: form.comentarios ?? undefined,
                    situacaoProposta: form.situacaoProposta,
                    statusParticipar: form.statusParticipar,
                    outros: form.outros ?? undefined,
                    observacao: form.observacao ?? undefined,
                    data: form.data ?? undefined,
                    cidade: form.cidade ?? undefined,
                    idFuncionario: form.idFuncionario ?? undefined,
                    telefone: form.telefone ?? undefined,
                    dadosCliente: form.dadosCliente ?? undefined,
                    dataSituacao: form.dataSituacao ?? undefined,

                    arquivo: form.arquivo ?? undefined,
                    nomeArquivo: form.nomeArquivo ?? undefined,
                    limparArquivo: form.limparArquivo ?? undefined,

                });

                await loadNovosNegocios(novosNegociosSelectedId);
                setNovosNegociosSelectedId(novosNegociosSelectedId);
                pushInfoNotification("Sucesso", "Novo negócio atualizado com sucesso!");
            } else {
                const idCliente = await createNovosNegociosUseCase.execute({
                    idNovosNegocios: novosNegociosSelectedId,
                    titulo: form.titulo ?? undefined,
                    idCliente: form.idCliente ?? undefined,
                    idCentroCusto: form.idCentroCusto ?? undefined,
                    nProposta: form.nProposta ?? undefined,
                    nEdital: form.nEdital ?? undefined,
                    possivelEscopo: form.possivelEscopo ?? undefined,
                    viavelFinanc: form.viavelFinanc ?? undefined,
                    possivelPrazos: form.possivelPrazos ?? undefined,
                    possivelReqLegais: form.possivelReqLegais ?? undefined,
                    comentarios: form.comentarios ?? undefined,
                    situacaoProposta: form.situacaoProposta,
                    statusParticipar: form.statusParticipar,
                    outros: form.outros ?? undefined,
                    observacao: form.observacao ?? undefined,

                    arquivo: form.arquivo ?? undefined,
                    nomeArquivo: form.nomeArquivo ?? undefined,

                    data: form.data ?? undefined,
                    cidade: form.cidade ?? undefined,
                    idFuncionario: form.idFuncionario ?? undefined,
                    telefone: form.telefone ?? undefined,
                    dadosCliente: form.dadosCliente ?? undefined,
                    dataSituacao: form.dataSituacao ?? undefined,

                });

                await loadNovosNegocios(idCliente);
                setNovosNegociosSelectedId(idCliente);
                pushInfoNotification("Sucesso", "Novo negócio salvo com sucesso!");
            }


        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar novo negócio!"));
        } finally {
            setSaving(false);
            setLoading(false);
        }
    }, [createNovosNegociosUseCase,
        updateNovosNegociosUseCase,
        novosNegociosSelectedId,
        loadNovosNegocios,
        pushInfoNotification,
        pushErrorNotification
    ]);


    const handleSalvarNovosNegociosArquivo = useCallback(async (form: NovosNegociosArquivoForm) => {
        try {
            setSaving(true);
            setLoading(true);
            if (!novosNegociosSelectedId) {
                pushErrorNotification("Erro", "Novo negócio não selecionado para adicionar arquivo!");
                return;
            }
            if (form.idNovosNegociosArquivo && form.idNovosNegociosArquivo > 0) {
                await updateNovosNegociosArquivoUseCase.execute(
                    form.idNovosNegociosArquivo,
                    novosNegociosSelectedId,
                    form.arquivo ?? undefined,
                    form.descricao ?? undefined,

                    form.limparArquivo ?? undefined

                );

            } else {
                await createNovosNegociosArquivoUseCase.execute(
                    novosNegociosSelectedId,
                    form.arquivo ?? undefined,
                    form.descricao ?? undefined,
                );
            }
            await loadAllNovosNegociosArquivo(novosNegociosSelectedId!);
            pushInfoNotification("Sucesso", "Arquivo do novo negócio salvo com sucesso!");
            setNovosNegociosArquivoModal(false);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar arquivo do novo negócio!"));
        } finally {
            setSaving(false);
            setLoading(false);
        }
    }, [
        createNovosNegociosArquivoUseCase,
        updateNovosNegociosArquivoUseCase,
        novosNegociosSelectedId,
        loadAllNovosNegociosArquivo,
        pushInfoNotification,
        pushErrorNotification,
    ]);

    //#endregion




    //#region MODAIS ARQUIVOS

    const handleNovosNegociosArquivoModalOpenState = useCallback(async (idNovosNegociosArquivo: number | null, openState: boolean) => {
        if (openState && idNovosNegociosArquivo) {
            await loadNovosNegociosArquivo(idNovosNegociosArquivo);
            setNovosNegociosArquivoModal(openState)
        } else {
            setNovosNegociosArquivoModal(openState)
            setNovosNegociosArquivo(undefined);
        }
    }, [
        loadNovosNegociosArquivo,
    ]);




    //#endregion




    //#region DELEÇÃO
    const handleNovosNegociosDeleteOpenState = useCallback((openState: boolean) => {
        if (openState && novosNegociosSelectedId) {
            setOpenDeleteNovosNegociosModal(openState)
        } else {
            setOpenDeleteNovosNegociosModal(openState)
        }
    }, [novosNegociosSelectedId]);

    const handleNovosNegociosArquivoDeleteOpenState = useCallback((idNovosNegociosArquivo: number | null, openState: boolean) => {
        if (openState && idNovosNegociosArquivo) {
            setOpenDeleteNovosNegociosArquivoModal(openState)
            setNovosNegociosArquivoSelectedIdDelete(idNovosNegociosArquivo)
        } else {
            setOpenDeleteNovosNegociosArquivoModal(openState)
            setNovosNegociosArquivoSelectedIdDelete(undefined)
        }
    }, []);



    const handleConfirmarDeletarNovosNegocios = useCallback(async () => {
        try {
            if (novosNegociosSelectedId) {
                await deleteNovosNegociosUseCase.execute(novosNegociosSelectedId);
                pushInfoNotification("Sucesso", "Novo negócio deletado com sucesso!");
                navigate("/NovosNegocios_lista");
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar novo negócio!"));
        }
    }, [novosNegociosSelectedId,
        deleteNovosNegociosUseCase,
        pushInfoNotification,
        pushErrorNotification,
        navigate]);



    const handleConfirmarDeletarNovosNegociosArquivo = useCallback(async () => {
        try {
            if (novosNegociosArquivoSelectedIdDelete) {
                await deleteNovosNegociosArquivoUseCase.execute(novosNegociosArquivoSelectedIdDelete);
                setNovosNegociosArquivoSelectedIdDelete(undefined);
                setOpenDeleteNovosNegociosArquivoModal(false);
                pushInfoNotification("Sucesso", "Arquivo do novo negócio deletado com sucesso!");
                await loadAllNovosNegociosArquivo(novosNegociosSelectedId!);
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar arquivo do novo negócio!"));
        }
    }, [
        novosNegociosArquivoSelectedIdDelete,
        deleteNovosNegociosArquivoUseCase,
        novosNegociosSelectedId,
        loadAllNovosNegociosArquivo,
        pushInfoNotification,
        pushErrorNotification,
    ]);


    //#endregion




    //#region ARQUIVOS

    const handleDownloadArquivo = useCallback(async () => {
        if (novosNegociosArquivo && novosNegociosArquivo.nome !== undefined && novosNegociosArquivo.arquivo) {
            try {
                setCustomLoading(true, 'Baixando arquivo...');
                await downloadBase64File(novosNegociosArquivo.nome, novosNegociosArquivo.arquivo);
            } catch (e: unknown) {
                console.log(e);
                pushErrorNotification("Erro", processError(e, "Esta funcionalidade não está em manutenção no momento."));
            } finally {
                setCustomLoading(false);
            }
        }
    }, [novosNegociosArquivo, pushErrorNotification, setCustomLoading]);

    const handleArquivoDeleteOpenState = useCallback((limparArquivos: string | null, arquivo: string | null, openState: boolean) => {
        if (openState && limparArquivos && arquivo) {
            setOpenDeleteArquivoModal(openState)
        } else {
            setOpenDeleteArquivoModal(openState)
        }
    }, []);

    const handleDownloadArquivoS3 = useCallback(async (idNovosNegociosArquivo: number, imageName: string) => {
        try {
            setCustomLoading(true, 'Baixando arquivo...');
            await getNovosNegociosArquivoByIdUseCase.execute(idNovosNegociosArquivo, imageName);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao baixar arquivo!"));
        } finally {
            setCustomLoading(false);
        }
    }, [getNovosNegociosArquivoByIdUseCase, pushErrorNotification, setCustomLoading]);

    const handleFileChange = useCallback((nomeArquivo: string, limparArquivos: string, e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
        if (!saving) {
            hookFormNovosNegocios.setValue(limparArquivos as any, false);
            hookFormNovosNegocios.setValue(nomeArquivo as any, e.target.files?.item(0)?.name ?? '');
            onChange(e.target.files?.item(0));
        }
    }, [
        saving,
        hookFormNovosNegocios,
    ]);


    const handleDeleteFile = useCallback(() => {
        if (!saving) {
            if (window.confirm('Você deseja excluir este item?')) {
                hookFormNovosNegocios.setValue('limparArquivo', true);
                hookFormNovosNegocios.setValue('nomeArquivo', null);
                hookFormNovosNegocios.setValue('arquivo', null);
                setNovosNegocios({
                    ...novosNegocios,
                    arquivo: undefined,
                    nomeArquivo: undefined,
                    limparArquivo: true,
                });
            }
        }
    }, [
        saving,
        hookFormNovosNegocios,
        novosNegocios,
    ]);

    //#endregion




    //#region HISTORICO

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(novosNegociosSelectedId ?? 0, LogTipoTabela.NOVOS_NEGOCIOS, true);
            }
        }
    }, [
        novosNegociosSelectedId,
        changeHistory,
    ]);

    //#endregion



    return {
        hookFormNovosNegocios,
        novosNegociosSelectedId,
        saving,
        loading,
        novosNegociosArquivo,
        novosNegociosArquivoLista,
        openDeleteNovosNegociosModal,
        openDeleteNovosNegociosArquivoModal,
        openNovosNegociosArquivoModal,
        ddlCentroCusto,
        ddlCliente,
        ddlFuncionario,
        novosNegociosArquivoSelectedIdDelete,
        openDeleteArquivoModal,
        changeHistory,
        openMenu,
        menuRef,
        pagination,
        situacaoProposta,
        novosNegocios,

        handleReload,
        handleFileChange,
        handleDeleteFile,
        handleNovosNegociosArquivoPageChange,
        handleNovosNegociosArquivoRowsPerPageChange,
        handleNovosNegociosArquivoDeleteOpenState,
        handleNovosNegociosArquivoModalOpenState,
        handleDownloadArquivoS3,
        handleOpenChangeHistory,
        handleDownloadArquivo,
        handleSalvarNovosNegocios: hookFormNovosNegocios.handleSubmit(handleSalvarNovosNegocios),
        handleSalvarNovosNegociosArquivo,
        handleNovosNegociosDeleteOpenState,
        handleConfirmarDeletarNovosNegociosArquivo,
        handleConfirmarDeletarNovosNegocios,
        handleArquivoDeleteOpenState,
        handlePageBack,
        handleCloseMenu,
        handleListKeyDown,
        handleToggleMenu,
        handleNewNovosNegocios,
    };
};



